import { useCallback, useEffect, useRef, useState } from "react";

export function useTruncationDetection(mode: "width" | "height") {
  const [isTruncated, setIsTruncated] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
    };
  }, []);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      resizeObserverRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();

      if (!node) {
        return;
      }

      const check = () => {
        const truncated =
          mode === "width"
            ? node.scrollWidth > node.clientWidth
            : node.scrollHeight > node.clientHeight;
        setIsTruncated((prev) => (prev !== truncated ? truncated : prev));
      };

      check();

      resizeObserverRef.current = new ResizeObserver(() => {
        check();
      });
      resizeObserverRef.current.observe(node);

      mutationObserverRef.current = new MutationObserver(() => {
        requestAnimationFrame(check);
      });
      mutationObserverRef.current.observe(node, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    },
    [mode],
  );

  return { ref, isTruncated };
}
